import { createAction, handleActions } from "redux-actions";
import extractApiError from "@utils/extract-api-error";
import {
    getMetricsMetricDataAPI,
    getMetricsMetricsAPI,
    getMetricsProjectsAPI,
    getMetricsTenantsAPI,
} from "./apis/usageMetrics";

// ===== Request Tracker 유틸 =====
const createRequestTracker = () => {
    let latestId = 0;
    let controller = null;

    return {
        start: () => {
            if (controller) {
                controller.abort();
            }

            const requestId = ++latestId;
            controller = new AbortController();

            return {
                id: requestId,
                signal: controller.signal,
                isLatest: () => requestId === latestId,
            };
        },
    };
};

const projectsTracker = createRequestTracker();
const metricDataTracker = createRequestTracker();

// ===== 기존 유틸 함수들 =====
const safeBigIntParse = (value) => {
    return BigInt(parseInt(value) || 0);
};

const bigIntSum = (existingValue, newValue) => {
    const existing = safeBigIntParse(existingValue);
    const additional = safeBigIntParse(newValue);
    return existing + additional;
};

const extractDateRangeInfo = (aggregationInfos, aggregationKey) => {
    if (!aggregationInfos || !aggregationInfos[aggregationKey]) {
        return null;
    }

    const params = aggregationInfos[aggregationKey].params;
    if (!params) {
        return null;
    }

    return {
        dateFrom: params.dateFrom,
        dateTo: params.dateTo,
        groupBy: params.groupBy,
    };
};

const initialState = {
    tenants: {
        pending: false,
        error: null,
        data: null,
    },
    projects: {
        pending: false,
        error: null,
        data: null,
    },
    metrics: {
        pending: false,
        error: null,
        data: null,
    },
    metricData: null,
};

const GET_METRICS_TENANTS_PENDING = "GET_METRICS_TENANTS_PENDING";
const GET_METRICS_TENANTS_FAILURE = "GET_METRICS_TENANTS_FAILURE";
const GET_METRICS_TENANTS_SUCCESS = "GET_METRICS_TENANTS_SUCCESS";
const GET_METRICS_PROJECTS_PENDING = "GET_METRICS_PROJECTS_PENDING";
const GET_METRICS_PROJECTS_SUCCESS = "GET_METRICS_PROJECTS_SUCCESS";
const GET_METRICS_PROJECTS_FAILURE = "GET_METRICS_PROJECTS_FAILURE";

const GET_METRICS_METRICS_PENDING = "GET_METRICS_METRICS_PENDING";
const GET_METRICS_METRICS_FAILURE = "GET_METRICS_METRICS_FAILURE";
const GET_METRICS_METRICS_SUCCESS = "GET_METRICS_METRICS_SUCCESS";
const GET_METRICS_METRIC_DATA_PENDING = "GET_METRICS_METRIC_DATA_PENDING";
const GET_METRICS_METRIC_DATA_FAILURE = "GET_METRICS_METRIC_DATA_FAILURE";
const GET_METRICS_METRIC_DATA_SUCCESS = "GET_METRICS_METRIC_DATA_SUCCESS";

const METRICS_RESET_PROJECTS = "METRICS_RESET_PROJECTS";
const METRICS_RESET = "METRICS_RESET";

export default handleActions(
    {
        [METRICS_RESET]: () => {
            return { ...initialState };
        },
        [GET_METRICS_TENANTS_PENDING]: (state) => {
            return { ...state, tenants: { ...initialState.tenants, pending: true } };
        },
        [GET_METRICS_TENANTS_SUCCESS]: (state, action) => {
            return { ...state, tenants: { ...state.tenants, data: action.payload, pending: false } };
        },
        [GET_METRICS_TENANTS_FAILURE]: (state, action) => {
            return { ...state, tenants: { ...state.tenants, pending: false, error: extractApiError(action.payload) } };
        },
        [METRICS_RESET_PROJECTS]: (state) => {
            return { ...state, projects: { ...initialState.projects } };
        },
        [GET_METRICS_PROJECTS_PENDING]: (state) => {
            return { ...state, projects: { ...initialState.projects, pending: true } };
        },
        [GET_METRICS_PROJECTS_SUCCESS]: (state, action) => {
            return { ...state, projects: { ...state.projects, data: action.payload, pending: false } };
        },
        [GET_METRICS_PROJECTS_FAILURE]: (state, action) => {
            return {
                ...state,
                projects: { ...state.projects, pending: false, error: extractApiError(action.payload) },
            };
        },
        [GET_METRICS_METRICS_PENDING]: (state) => {
            return { ...state, metrics: { ...initialState.metrics, pending: true } };
        },
        [GET_METRICS_METRICS_SUCCESS]: (state, action) => {
            return { ...state, metrics: { ...state.metrics, data: action.payload, pending: false } };
        },
        [GET_METRICS_METRICS_FAILURE]: (state, action) => {
            return { ...state, metrics: { ...state.metrics, pending: false, error: extractApiError(action.payload) } };
        },
        [GET_METRICS_METRIC_DATA_PENDING]: (state, action) => {
            const { key } = action.payload;
            return {
                ...state,
                metricData: {
                    ...state.metricData,
                    [key]: { ...(initialState.metricData?.[key] ?? {}), pending: true },
                },
            };
        },
        [GET_METRICS_METRIC_DATA_SUCCESS]: (state, action) => {
            const { key, data } = action.payload;
            return {
                ...state,
                metricData: {
                    ...state.metricData,
                    [key]: { data, pending: false, error: false },
                },
            };
        },
        [GET_METRICS_METRIC_DATA_FAILURE]: (state, action) => {
            const { key, error } = action.payload;
            return {
                ...state,
                metricData: {
                    ...state.metricData,
                    [key]: { ...state.metricData[key], pending: false, error },
                },
            };
        },
    },
    initialState,
);

export const resetMetricsProjects = createAction(METRICS_RESET_PROJECTS);
export const resetMetrics = createAction(METRICS_RESET);

export const getMetricsTenants = () => async (dispatch, getState) => {
    const { stage } = getState();
    dispatch({ type: GET_METRICS_TENANTS_PENDING });
    try {
        let nextToken = null;
        let tenants = [];
        do {
            const response = await getMetricsTenantsAPI(stage.endpoint, { nextToken });
            tenants = tenants.concat(response.data.results);
            nextToken = response.data.nextToken;
        } while (nextToken);
        dispatch({ type: GET_METRICS_TENANTS_SUCCESS, payload: tenants });
        return tenants;
    } catch (error) {
        dispatch({ type: GET_METRICS_TENANTS_FAILURE, payload: error });
        return [];
    }
};

// ===== Projects 조회 (Request Tracker 적용) =====
export const getMetricsProjects = (tenantId) => async (dispatch, getState) => {
    const request = projectsTracker.start();
    const { stage } = getState();

    dispatch({ type: GET_METRICS_PROJECTS_PENDING });

    try {
        let nextToken = null;
        let projects = [];

        do {
            if (request.signal.aborted) {
                throw new DOMException("Aborted", "AbortError");
            }

            const response = await getMetricsProjectsAPI(stage.endpoint, {
                nextToken,
                tenantIds: tenantId,
            });
            projects = projects.concat(response.data.results);
            nextToken = response.data.nextToken;
        } while (nextToken);

        if (request.isLatest()) {
            dispatch({ type: GET_METRICS_PROJECTS_SUCCESS, payload: projects });
        }
    } catch (error) {
        if (error.name === "AbortError") {
            return;
        }

        if (request.isLatest()) {
            dispatch({ type: GET_METRICS_PROJECTS_FAILURE, payload: error });
        }
    }
};

export const getMetricsMetrics = () => async (dispatch, getState) => {
    const { stage } = getState();
    dispatch({ type: GET_METRICS_METRICS_PENDING });
    try {
        let nextToken = null;
        let metrics = [];
        do {
            const response = await getMetricsMetricsAPI(stage.endpoint, { nextToken });
            metrics = metrics.concat(response.data.results);
            nextToken = response.data.nextToken;
        } while (nextToken);

        dispatch({ type: GET_METRICS_METRICS_SUCCESS, payload: metrics });
    } catch (error) {
        dispatch({ type: GET_METRICS_METRICS_FAILURE, payload: error });
    }
};

async function fetchMetricDataWithConfig(metricsResponse, config, endpoint, signal) {
    const selectedIds = config.selector(metricsResponse);

    const requests = selectedIds.map(async (metricId) => {
        const params = config.params || {};

        let metricData = [];
        let nextToken = null;

        try {
            do {
                if (signal?.aborted) {
                    throw new DOMException("Aborted", "AbortError");
                }

                const response = await getMetricsMetricDataAPI(endpoint, metricId, {
                    ...params,
                    nextToken,
                });
                metricData = metricData.concat(response.data.results);
                nextToken = response.data.nextToken;
            } while (nextToken);

            return { success: true, metricId, data: metricData };
        } catch (error) {
            if (error.name === "AbortError") {
                throw error;
            }
            return { success: false, metricId, data: metricData, error };
        }
    });

    return Promise.allSettled(requests);
}

const aggregateByKey = (data, groupByKey) => {
    const result = {};

    data.forEach((item) => {
        const key = groupByKey.split(".").reduce((obj, prop) => obj?.[prop], item);
        const keyStr = key ?? "null";

        result[keyStr] = bigIntSum(result[keyStr], item.value);
    });

    Object.keys(result).forEach((key) => {
        result[key] = result[key].toString();
    });
    return result;
};

const aggregateMetricData = (aggregatedData, metricIds, aggregator) => {
    const result = {};

    metricIds.forEach((metricId) => {
        const sourceData = aggregatedData?.[metricId] || [];

        sourceData.forEach((item) => {
            aggregator(result, item, metricId);
        });
    });

    return result;
};

const processSummaryData = (prevMonthlyData, monthlyData, prevAggregationInfo, currentAggregationInfo) => {
    const groupByKey = currentAggregationInfo?.params?.tenantIds ? "project" : "tenant";

    // 이전 달 데이터 처리
    const prevMonthMap = new Map();
    let prevMonth = null;

    Object.entries(prevMonthlyData).forEach(([metricId, dataArray]) => {
        dataArray.forEach((item) => {
            const month = item.datetime;
            prevMonth = month;

            let groupId;
            if (groupByKey === "project") {
                groupId = item.project?.id || "unknown";
            } else {
                groupId = item.tenant?.id || "unknown";
            }

            if (!prevMonthMap.has(groupId)) {
                prevMonthMap.set(groupId, new Map());
            }
            const groupMap = prevMonthMap.get(groupId);

            const currentValue = groupMap.get(metricId) || BigInt(0);
            const newValue = safeBigIntParse(item.value);
            groupMap.set(metricId, currentValue + newValue);
        });
    });

    // 현재 달 데이터 처리
    const currentMonthMap = new Map();
    let currentMonth = null;

    Object.entries(monthlyData).forEach(([metricId, dataArray]) => {
        dataArray.forEach((item) => {
            const month = item.datetime;
            currentMonth = month;

            let groupId;
            if (groupByKey === "project") {
                groupId = item.project?.id || "unknown";
            } else {
                groupId = item.tenant?.id || "unknown";
            }

            if (!currentMonthMap.has(groupId)) {
                currentMonthMap.set(groupId, new Map());
            }
            const groupMap = currentMonthMap.get(groupId);

            const currentValue = groupMap.get(metricId) || BigInt(0);
            const newValue = safeBigIntParse(item.value);
            groupMap.set(metricId, currentValue + newValue);
        });
    });

    // 이전 달 전체 합계 계산
    const prevMonthTotal = new Map();
    prevMonthMap.forEach((groupMap) => {
        groupMap.forEach((value, metricId) => {
            const currentTotal = prevMonthTotal.get(metricId) || BigInt(0);
            prevMonthTotal.set(metricId, currentTotal + value);
        });
    });

    // 현재 달 전체 합계 계산
    const currentMonthTotal = new Map();
    currentMonthMap.forEach((groupMap) => {
        groupMap.forEach((value, metricId) => {
            const currentTotal = currentMonthTotal.get(metricId) || BigInt(0);
            currentMonthTotal.set(metricId, currentTotal + value);
        });
    });

    // 결과를 일반 객체로 변환 (Redux state에 저장하기 위해)
    const result = {
        monthly: {},
        currentMonth: {},
        prevMonth: {},
    };

    // 월별 데이터 변환
    if (prevMonth) {
        result.monthly[prevMonth] = {};
        prevMonthMap.forEach((groupMap, groupId) => {
            result.monthly[prevMonth][groupId] = {};
            groupMap.forEach((value, metricId) => {
                result.monthly[prevMonth][groupId][metricId] = value.toString();
            });
        });
    }

    if (currentMonth) {
        result.monthly[currentMonth] = {};
        currentMonthMap.forEach((groupMap, groupId) => {
            result.monthly[currentMonth][groupId] = {};
            groupMap.forEach((value, metricId) => {
                result.monthly[currentMonth][groupId][metricId] = value.toString();
            });
        });
    }

    // 현재 달 합계 변환
    currentMonthTotal.forEach((value, metricId) => {
        result.currentMonth[metricId] = value.toString();
    });

    // 이전 달 합계 변환
    prevMonthTotal.forEach((value, metricId) => {
        result.prevMonth[metricId] = value.toString();
    });

    return result;
};

const generateDateRange = (dateFrom, dateTo, groupBy) => {
    const dates = [];
    const start = new Date(dateFrom);
    const end = new Date(dateTo);

    if (groupBy === "DAY") {
        const currentDate = new Date(start);
        while (currentDate <= end) {
            const year = currentDate.getFullYear();
            const month = String(currentDate.getMonth() + 1).padStart(2, "0");
            const day = String(currentDate.getDate()).padStart(2, "0");
            dates.push(`${year}-${month}-${day}`);
            currentDate.setDate(currentDate.getDate() + 1);
        }
    } else if (groupBy === "MONTH") {
        const currentDate = new Date(start.getFullYear(), start.getMonth(), 1);
        const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
        while (currentDate <= endMonth) {
            const year = currentDate.getFullYear();
            const month = String(currentDate.getMonth() + 1).padStart(2, "0");
            dates.push(`${year}-${month}`);
            currentDate.setMonth(currentDate.getMonth() + 1);
        }
    }

    return dates;
};

const processChartByDatetime = (aggregatedData, metricIds, dateRangeInfo = null) => {
    const sourceDataByMetric = {};

    metricIds.forEach((metricId) => {
        const sourceData = aggregatedData?.[metricId] || [];
        const sumByDatetime = aggregateByKey(sourceData, "datetime");
        sourceDataByMetric[metricId] = sumByDatetime;
    });

    let datetimeList;

    if (dateRangeInfo && dateRangeInfo.dateFrom && dateRangeInfo.dateTo && dateRangeInfo.groupBy) {
        datetimeList = generateDateRange(dateRangeInfo.dateFrom, dateRangeInfo.dateTo, dateRangeInfo.groupBy);

        return datetimeList.map((datetime) => {
            const dataPoint = { datetime };
            metricIds.forEach((metricId) => {
                dataPoint[metricId] = sourceDataByMetric[metricId]?.[datetime] || 0;
            });
            return dataPoint;
        });
    }

    return [];
};

const processChartByDimension = (aggregatedData, metricIds, groupBy) => {
    const result = aggregateMetricData(aggregatedData, metricIds, (acc, item) => {
        const dimensionValue = item[groupBy] || "unknown";
        acc[dimensionValue] = bigIntSum(acc[dimensionValue], item.value);
    });

    return Object.entries(result).map(([name, value]) => ({
        name,
        value: value.toString(),
    }));
};

const processChartByTenant = (aggregatedData, metricIds, groupBy) => {
    const resultByGroup = aggregateMetricData(aggregatedData, metricIds, (acc, item, metricId) => {
        const groupValue = item[groupBy].id || item[groupBy] || "unknown";
        const groupName = item[groupBy].name || item[groupBy] || "unknown";

        if (!acc[groupValue]) {
            acc[groupValue] = { id: groupValue, name: groupName };
        }

        acc[groupValue][metricId] = bigIntSum(acc[groupValue][metricId], item.value);
    });

    Object.values(resultByGroup).forEach((group) => {
        metricIds.forEach((metricId) => {
            if (group[metricId] !== undefined) {
                group[metricId] = group[metricId].toString();
            }
        });
    });

    return Object.values(resultByGroup);
};

const processChartByDatetimeAndSecondary = (aggregatedData, metricIds, secondaryGroupBy, dateRangeInfo) => {
    const secondaryKeySet = new Set();

    const resultByDatetime = aggregateMetricData(aggregatedData, metricIds, (acc, item) => {
        const datetime = item.datetime;
        const secondaryValue = item[secondaryGroupBy]?.name || item[secondaryGroupBy] || "unknown";

        secondaryKeySet.add(secondaryValue);

        if (!acc[datetime]) {
            acc[datetime] = { datetime };
        }

        acc[datetime][secondaryValue] = bigIntSum(acc[datetime][secondaryValue], item.value);
    });

    let datetimeList = [];
    if (dateRangeInfo && dateRangeInfo.dateFrom && dateRangeInfo.dateTo && dateRangeInfo.groupBy) {
        datetimeList = generateDateRange(dateRangeInfo.dateFrom, dateRangeInfo.dateTo, dateRangeInfo.groupBy);
    } else {
        datetimeList = Object.keys(resultByDatetime).sort();
    }

    return datetimeList.map((datetime) => {
        const dataPoint = { datetime };
        secondaryKeySet.forEach((secondaryValue) => {
            const value = resultByDatetime[datetime]?.[secondaryValue];
            dataPoint[secondaryValue] = value ? value.toString() : "0";
        });
        return dataPoint;
    });
};

const processChartRaw = (aggregatedData, metricIds) => {
    if (metricIds.length === 0) return [];

    const metricId = metricIds[0];
    return aggregatedData?.[metricId] || [];
};

const dataProcessors = {
    summary: (rawData, config, aggregationInfos) => {
        const { aggregationKey } = config;

        // 단일 키 (문자열)
        if (!Array.isArray(aggregationKey)) {
            if (!rawData[aggregationKey]) return null;
            return processSummaryData(rawData[aggregationKey], aggregationInfos[aggregationKey]);
        } else {
            const [prevKey, currentKey] = aggregationKey;
            const prevMonthlyData = rawData[prevKey];
            const monthlyData = rawData[currentKey];

            if (!prevMonthlyData || !monthlyData) return null;

            return processSummaryData(
                prevMonthlyData,
                monthlyData,
                aggregationInfos[prevKey],
                aggregationInfos[currentKey],
            );
        }
    },
    chart: (rawData, config, aggregationInfos) => {
        const { aggregationKey, metricIds, groupBy } = config;

        // 배열이면 첫 번째 요소만 사용 (chart는 단일 aggregation만 사용)
        const key = Array.isArray(aggregationKey) ? aggregationKey[0] : aggregationKey;

        // aggregationKey에 해당하는 데이터만 추출하고 날짜 조건이 걸려있을 경우 필터링
        let aggregatedData = rawData[key] || {};

        if (!groupBy) {
            return processChartRaw(aggregatedData, metricIds);
        }

        if (groupBy.startsWith("datetime")) {
            const parts = groupBy.split(".");
            const secondaryGroupBy = parts[1];
            const dateRangeInfo = extractDateRangeInfo(aggregationInfos, key);

            if (secondaryGroupBy) {
                return processChartByDatetimeAndSecondary(aggregatedData, metricIds, secondaryGroupBy, dateRangeInfo);
            } else {
                return processChartByDatetime(aggregatedData, metricIds, dateRangeInfo);
            }
        }

        if (groupBy === "dimension" || groupBy === "subDimension") {
            return processChartByDimension(aggregatedData, metricIds, groupBy);
        }

        if (groupBy === "tenant" || groupBy === "project") {
            return processChartByTenant(aggregatedData, metricIds, groupBy);
        }

        return processChartRaw(aggregatedData, metricIds);
    },
};

// ===== MetricData 조회 (Request Tracker 적용) =====
export const getMetricsMetricData =
    (aggregationInfos, processConfigs = []) =>
    async (dispatch, getState) => {
        const request = metricDataTracker.start();
        const { stage, usageMetrics } = getState();

        try {
            processConfigs.forEach((config) => {
                dispatch({ type: GET_METRICS_METRIC_DATA_PENDING, payload: { key: config.key } });
            });

            const aggregationPromises = Object.entries(aggregationInfos).map(async ([key, config]) => {
                try {
                    const results = await fetchMetricDataWithConfig(
                        usageMetrics.metrics.data || [],
                        config,
                        stage.endpoint,
                        request.signal,
                    );

                    const allData = {};
                    results.forEach((result) => {
                        if (result.status === "fulfilled") {
                            const { metricId, data } = result.value;
                            allData[metricId] = data;
                        }
                    });

                    return { key, success: true, data: allData };
                } catch (error) {
                    if (error.name === "AbortError") {
                        return { key, success: false, aborted: true };
                    }
                    return { key, success: false, error };
                }
            });

            const results = await Promise.allSettled(aggregationPromises);

            const finalResults = {};
            results.forEach((result) => {
                if (result.status === "fulfilled" && result.value.success) {
                    const { key, data } = result.value;
                    finalResults[key] = data;
                }
            });

            processConfigs.forEach((config) => {
                try {
                    const processorFn = dataProcessors[config.processorType];
                    if (!processorFn) {
                        throw new Error(`Unknown processor type: ${config.processorType}`);
                    }

                    const processedData = processorFn(finalResults, config, aggregationInfos);

                    if (processedData === null || processedData === undefined) {
                        throw new Error("No data to process");
                    }

                    if (request.isLatest()) {
                        dispatch({
                            type: GET_METRICS_METRIC_DATA_SUCCESS,
                            payload: { key: config.key, data: processedData },
                        });
                    } else {
                        console.log("past data", processedData);
                    }
                } catch (error) {
                    if (request.isLatest()) {
                        dispatch({
                            type: GET_METRICS_METRIC_DATA_FAILURE,
                            payload: { key: config.key, error: error.message },
                        });
                    }
                }
            });

            return finalResults;
        } catch (error) {
            if (error.name === "AbortError") {
                return {};
            }
            console.error("getMetricsMetricData error:", error);
            return {};
        }
    };
